import type { SxProps, Theme } from '@mui/material';
import { Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { FC, ReactNode } from 'react';
import { ConfirmationDialog, TextField } from 'components/common';
import { addFacebookPageValidator } from '../validation';
import type { z } from 'zod';
import type { AddFacebookPageResponse } from '../api';
import { useAddFacebookPage, useUpdateFacebookPage } from '../api';
import { useEnqueueSnackbar, useModal } from 'hooks';
import { useNavigate } from 'react-router';
import type { FacebookPage } from '../types';
import { isEmpty } from 'lodash-es';
import DeleteFacebookPage from './DeleteFacebookPage';
import { usePermissionGuard } from 'features/login';
import {
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';

type FormData = z.infer<typeof addFacebookPageValidator.schema>;

interface FacebookPageFormProps {
  data?: FacebookPage;
  onSuccess?: (data: AddFacebookPageResponse) => void;
  sx?: SxProps<Theme>;
  titleForm?: ReactNode;
}

const AddFacebookPageForm: FC<FacebookPageFormProps> =
  ({
     data,
     onSuccess,
     sx,
     titleForm,
   }) => {
    const { accessToDeleteFacebookpage } = usePermissionGuard();
    const isEditMode = !isEmpty(data);
    const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
    const navigate = useNavigate();
    const { open, close, isOpen } = useModal();

    const addFacebookPage = useAddFacebookPage({
      config: createCabinetMutationTracking<AddFacebookPageResponse>({
        platform: 'facebook',
        entity: 'facebook_page',
        mode: 'create',
        onSuccess: (data: AddFacebookPageResponse) => {
          onShowAlert('Facebook page added successfully');
          !onSuccess && navigate('/cabinet/facebook/facebook-pages');
          onSuccess?.(data);
        },
        onError: (error) => {
          onShowErrorAlert(error.message);
        },
      }),
    });

    const updateFacebookPage = useUpdateFacebookPage({
      config: createCabinetMutationTracking<AddFacebookPageResponse>({
        platform: 'facebook',
        entity: 'facebook_page',
        mode: 'edit',
        onSuccess: (data: AddFacebookPageResponse) => {
          onShowAlert('Facebook page updated successfully');
          !onSuccess && navigate('/cabinet/facebook/facebook-pages');
          onSuccess?.(data);
        },
        onError: (error) => {
          onShowErrorAlert(error.message || 'Failed to update Facebook page');
        },
      }),
    });

    const formMethods = useForm<FormData>({
      resolver: addFacebookPageValidator.resolver,
      defaultValues: {
        facebook_page_name: data?.facebook_page_name ?? '',
        facebook_page_id: data?.facebook_page_id ?? '',
        instagram_page_id: data?.instagram_page_id ?? '',
      },
    });

    const { handleSubmit } = formMethods;

    const onSubmit =
      ({ facebook_page_name, facebook_page_id, instagram_page_id }: FormData) => {
        trackCabinetSubmitAttempt(
          'facebook',
          'facebook_page',
          isEditMode ? 'edit' : 'create',
        );

        if (isEditMode) {
          updateFacebookPage.mutate({
            id: data.id,
            facebook_page_name,
            facebook_page_id,
            instagram_page_id,
          });
          return;
        }

        addFacebookPage.mutate({
          facebook_page_name,
          facebook_page_id,
          instagram_page_id,
        });
      };

    const isPending = isEditMode
      ? updateFacebookPage.isPending
      : addFacebookPage.isPending;

    return (
      <FormProvider {...formMethods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxWidth: '40%',
            alignItems: 'start',
            justifyContent: 'start',
            ...sx,
          }}
        >
          {titleForm ?? (
            <Typography variant={'subtitle1'}>
              {isEditMode ? 'Edit Facebook Page' : 'Add Facebook Page'}
            </Typography>
          )}
          <TextField
            name={'facebook_page_name'}
            inputLabel={'Facebook Page Name'}
            placeholder={'Value'}
            inputProps={{ autoFocus: true }}
          />
          <TextField
            name={'facebook_page_id'}
            inputLabel={'Facebook Page ID'}
            placeholder={'Value'}
          />
          <TextField
            name={'instagram_page_id'}
            inputLabel={'Instagram Account ID'}
            placeholder={'Value'}
          />

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Button
              startIcon={
                isPending && <CircularProgress color={'inherit'} size={18} />
              }
              onClick={handleSubmit(onSubmit)}
              variant={'contained'}
              disabled={isPending}
            >
              {isEditMode ? 'Update Facebook Page' : 'Save Facebook Page'}
            </Button>
            {isEditMode && data && (
              <>
                <Button
                  variant={'outlined'}
                  color={'error'}
                  onClick={open}
                  disabled={isPending || !accessToDeleteFacebookpage}
                >
                  Delete
                </Button>
                {accessToDeleteFacebookpage && <DeleteFacebookPage
                  isOpen={isOpen}
                  close={close}
                  id={data.id}
                  name={data.facebook_page_name}
                />}
              </>
            )}
          </Box>
        </Box>
        <ConfirmationDialog isUnblock={addFacebookPage.isSuccess || updateFacebookPage.isSuccess} />
      </FormProvider>
    );
  };

export default AddFacebookPageForm;
